import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Stack, Typography, Paper, IconButton, CircularProgress, Chip,
  Button, ToggleButton, ToggleButtonGroup, Tooltip, Divider,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRightOutlined";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isSameDay, isBefore, isAfter,
  startOfDay,
} from "date-fns";
import { apiGet, apiPost, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

// eBay holds funds and pays out 2 days after the carrier confirms delivery.
// Surfaced here purely for copy in the help tooltip + legend; the actual
// payout date math lives server-side in /api/funds so client doesn't have
// to re-derive it.
const PAYOUT_DELAY_DAYS = 2;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Date strings from /api/funds are always "YYYY-MM-DD" (server emits
// ``date.isoformat()``). Pin to local noon so day-grid math doesn't
// shift across timezones.
function parseFundsDate(s) {
  if (!s) return null;
  return new Date(s + "T12:00:00");
}

// Operator asked to drop "estimated" and "unfulfilled" buckets — only
// these three remain. ``no_eta`` is anything with no payout date
// (no tracking yet, or carrier returned no ETA/delivered_at).
const KIND_META = {
  delivered:  { label: "Delivered",  color: "success" },
  in_transit: { label: "In transit", color: "info"    },
  no_eta:     { label: "No ETA",     color: "warning" },
};

export default function Funds() {
  const { error: toastError } = useToast();
  const [funds, setFunds] = useState({ rows: [], totals: null, by_kind: {}, cache: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  // "all" | "delivered" | "in_transit" — trim the calendar to higher-
  // confidence buckets when the user wants a conservative cash-flow view.
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  // Background-prefetch state. We no longer poll /api/tracking/prefetch_status
  // every 4 seconds — that was causing the page to constantly flicker and
  // re-fetch /api/funds. Instead we read the auto_refresh hint from the
  // /api/funds response and show a passive inline indicator until the
  // operator clicks "Refresh" again. Stale carrier ETAs catch up on
  // their own via the 30-min shipment_cache TTL.
  const [refreshHint, setRefreshHint] = useState(null);  // { total } | null

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await apiGet("/api/funds", { signal });
      if (signal?.aborted) return;
      // Hydrate payout_date strings into Date objects once, up-front, so
      // every render path can rely on `.payoutDate` being a real Date.
      const rows = (res?.rows || []).map((r) => ({
        ...r,
        payoutDate: parseFundsDate(r.payout_date),
      }));
      setFunds({
        rows,
        totals: res?.totals || null,
        by_kind: res?.by_kind || {},
        cache:  res?.cache  || null,
      });
      setRefreshHint(
        res?.auto_refresh?.started
          ? { total: res.auto_refresh.total || 0 }
          : null,
      );
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load funds", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Manual refresh: kick the background tracking prefetch on the server
  // and reload /api/funds. The user gets a passive "refreshing N…" badge
  // until the next page load picks up the warmed cache; no interval
  // polling, no auto-reload that interrupts whatever the user is doing.
  const refresh = async () => {
    setRefreshing(true);
    try {
      await apiPost("/api/tracking/prefetch", {});
      await load();
    } catch (e) {
      toastError("Couldn't kick off refresh", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  // Apply the confidence filter to whatever feeds the calendar/totals.
  const visible = useMemo(() => {
    const rows = funds.rows;
    if (confidenceFilter === "all") return rows;
    if (confidenceFilter === "delivered") return rows.filter((r) => r.kind === "delivered");
    if (confidenceFilter === "in_transit")
      return rows.filter((r) => r.kind === "in_transit" || r.kind === "delivered");
    return rows;
  }, [funds.rows, confidenceFilter]);

  // Bucket scheduled payouts by ISO date for O(1) calendar lookup.
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of visible) {
      if (!r.payoutDate) continue;
      const key = format(r.payoutDate, "yyyy-MM-dd");
      const arr = m.get(key) || [];
      arr.push(r);
      m.set(key, arr);
    }
    return m;
  }, [visible]);

  const tbd = useMemo(() => visible.filter((r) => !r.payoutDate), [visible]);
  const today = useMemo(() => new Date(), []);

  // Header tile totals — always reflect the visible (filtered) set.
  const totals = useMemo(() => {
    let scheduled = 0, scheduledCount = 0, tbdAmt = 0;
    for (const r of visible) {
      if (r.payoutDate) { scheduled += r.amount; scheduledCount += 1; }
      else { tbdAmt += r.amount; }
    }
    let month = 0, monthCount = 0;
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    for (const r of visible) {
      if (!r.payoutDate) continue;
      if (!isBefore(r.payoutDate, monthStart) && !isAfter(r.payoutDate, monthEnd)) {
        month += r.amount; monthCount += 1;
      }
    }
    return { scheduled, scheduledCount, tbdAmt, tbdCount: tbd.length, month, monthCount };
  }, [visible, tbd, cursor]);

  // Calendar grid: full weeks from start-of-month-week through end-of-month-week.
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    const days = [];
    let d = start;
    while (!isAfter(d, end)) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [cursor]);

  const goPrevMonth = () => { setCursor((c) => addMonths(c, -1)); setSelectedDay(null); };
  const goNextMonth = () => { setCursor((c) => addMonths(c, 1)); setSelectedDay(null); };
  const goToday = () => { setCursor(startOfMonth(new Date())); setSelectedDay(startOfDay(new Date())); };

  const selectedKey = selectedDay ? format(selectedDay, "yyyy-MM-dd") : null;
  const selectedRows = selectedKey ? byDay.get(selectedKey) || [] : [];

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
              Funds
            </Typography>
            <Tooltip title={`Available + On Hold come straight from eBay's Sell Finances API and match Seller Hub exactly. Each on-hold order's payout date is projected as carrier delivery + ${PAYOUT_DELAY_DAYS}d (USPS via the public-page scraper, FedEx/UPS via API).`}>
              <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary", mt: -0.5 }} />
            </Tooltip>
          </Stack>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            eBay payouts — Available now + projected from on-hold orders (delivery + {PAYOUT_DELAY_DAYS}d)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Passive indicator that a background tracking refresh was
              kicked off by the last /api/funds call. No polling — the
              next manual refresh / page reload will pick up the warmed
              cache automatically. */}
          {refreshHint && (
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mr: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Refreshing {refreshHint.total} tracking{refreshHint.total === 1 ? "" : "s"} in background
              </Typography>
            </Stack>
          )}
          {/* Cache stats — visible only when something was processed
              fresh, so the operator can see when the cache hit-rate is
              good (mostly hits) vs the first cold load (mostly
              processed). Helps reason about why a load was slow. */}
          {funds.cache && (funds.cache.processed > 0 || funds.cache.hits > 0) && (
            <Tooltip title={`First load is slow (every order processed cold). Subsequent loads short-circuit through the per-order cache and only re-process new + stale orders.`}>
              <Chip
                size="small"
                variant="outlined"
                label={`${funds.cache.hits} cached · ${funds.cache.processed} fresh`}
                sx={{ height: 24, fontSize: 11 }}
              />
            </Tooltip>
          )}
          <Button
            startIcon={refreshing ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={refresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh tracking"}
          </Button>
        </Stack>
      </Stack>

      {/* Totals tiles — Available and On Hold come straight from eBay's
          Sell Finances API and exactly match the Seller Hub funds view.
          Scheduled / This month / TBD describe the *projection* of when
          the on-hold pool will release. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, borderColor: "success.light" }}>
          <Typography variant="caption" sx={{ color: "success.main", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
            Available
          </Typography>
          <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", mt: 0.5, color: "success.dark" }}>
            {money(funds.totals?.available)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Today's payout from eBay
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
            On hold
          </Typography>
          <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", mt: 0.5 }}>
            {money(funds.totals?.on_hold)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {funds.totals?.on_hold_count || 0} order{funds.totals?.on_hold_count === 1 ? "" : "s"} pending delivery
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
            {format(cursor, "LLLL yyyy")}
          </Typography>
          <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", mt: 0.5 }}>
            {money(totals.month)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {totals.monthCount} payout{totals.monthCount === 1 ? "" : "s"} this month
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
            TBD
          </Typography>
          <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", mt: 0.5, color: "warning.main" }}>
            {money(totals.tbdAmt)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {totals.tbdCount} no ETA / past-due ETA
          </Typography>
        </Paper>
      </Box>

      {/* Cancellation summary — only when there's something to report.
          Tells the operator how many orders we dropped on purpose
          (canceled or fully-refunded) so they don't wonder where they
          went. Server already filters these out of the projection. */}
      {((funds.totals?.canceled_count || 0) + (funds.totals?.refunded_count || 0)) > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, borderColor: "divider", bgcolor: "grey.50" }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Dropped from projection:{" "}
            {funds.totals?.canceled_count > 0 && (
              <strong>{funds.totals.canceled_count} canceled</strong>
            )}
            {funds.totals?.canceled_count > 0 && funds.totals?.refunded_count > 0 && " · "}
            {funds.totals?.refunded_count > 0 && (
              <strong>{funds.totals.refunded_count} fully refunded</strong>
            )}
            {funds.totals?.orders_pulled > 0 && (
              <> · pulled {funds.totals.orders_pulled} orders from eBay (past {funds.totals.days_back}d)</>
            )}
          </Typography>
        </Paper>
      )}

      {/* Loading state — only shows during the initial cold load. Once
          rows are in the response we render those instead, even if the
          background tracking refresh is still going. */}
      {loading && funds.rows.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, mb: 2, textAlign: "center" }}>
          <Stack alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Pulling eBay orders + per-order shipment lookups (first load takes ~30s, subsequent loads are cached)…
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Calendar + side panel */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          {/* Calendar header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={goPrevMonth} aria-label="Previous month">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 160, textAlign: "center" }}>
                {format(cursor, "LLLL yyyy")}
              </Typography>
              <IconButton size="small" onClick={goNextMonth} aria-label="Next month">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              <Button size="small" onClick={goToday} sx={{ ml: 0.5 }}>Today</Button>
            </Stack>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={confidenceFilter}
              onChange={(_, v) => v && setConfidenceFilter(v)}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="in_transit">Tracked</ToggleButton>
              <ToggleButton value="delivered">Confirmed</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Weekday header */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <Box key={d} sx={{ px: 1, py: 0.5, textAlign: "center", fontSize: 11, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {d}
              </Box>
            ))}
          </Box>

          {/* Day cells */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {calendarDays.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const rows = byDay.get(key) || [];
              const total = rows.reduce((s, r) => s + r.amount, 0);
              const inMonth = isSameMonth(d, cursor);
              const isToday = isSameDay(d, today);
              const isSelected = selectedDay && isSameDay(d, selectedDay);
              const hasDelivered = rows.some((r) => r.kind === "delivered");
              const hasTransit   = rows.some((r) => r.kind === "in_transit");
              return (
                <Box
                  key={key}
                  onClick={() => setSelectedDay(rows.length ? d : null)}
                  sx={{
                    minHeight: 88,
                    p: 0.75,
                    border: 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderWidth: isSelected ? 2 : 1,
                    borderRadius: 1,
                    bgcolor: inMonth ? "background.paper" : "grey.50",
                    cursor: rows.length ? "pointer" : "default",
                    opacity: inMonth ? 1 : 0.55,
                    "&:hover": rows.length ? { bgcolor: "action.hover" } : undefined,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 700 : 500,
                        color: isToday ? "primary.main" : (inMonth ? "text.primary" : "text.disabled"),
                        ...(isToday && {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          width: 22, height: 22, borderRadius: "50%",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                        }),
                      }}
                    >
                      {format(d, "d")}
                    </Typography>
                    {rows.length > 0 && (
                      <Stack direction="row" gap={0.25}>
                        {hasDelivered && (
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "success.main" }} />
                        )}
                        {hasTransit && (
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "info.main" }} />
                        )}
                      </Stack>
                    )}
                  </Stack>
                  {total > 0 && (
                    <>
                      <Typography
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 700,
                          fontSize: 14,
                          color: hasDelivered && !hasTransit
                            ? "success.dark"
                            : (hasTransit || hasDelivered)
                              ? "info.dark"
                              : "text.secondary",
                          lineHeight: 1.1,
                        }}
                      >
                        {money(total)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                        {rows.length} order{rows.length === 1 ? "" : "s"}
                      </Typography>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Legend — only the two buckets that can land on a date. The
              third bucket (no_eta) doesn't have a payout date so it
              doesn't appear on the calendar; it shows up in the TBD
              tile instead. */}
          <Stack direction="row" gap={2} sx={{ mt: 1.5 }} flexWrap="wrap">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>Delivered (delivered_at + {PAYOUT_DELAY_DAYS}d)</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "info.main" }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>In transit (ETA + {PAYOUT_DELAY_DAYS}d)</Typography>
            </Stack>
          </Stack>
        </Paper>

        {/* Side panel */}
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
              {selectedDay ? format(selectedDay, "EEEE, LLL d") : "Pick a day"}
            </Typography>
            {!selectedDay && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                Click a day on the calendar to see which orders contribute to that payout.
              </Typography>
            )}
            {selectedDay && selectedRows.length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                No payouts scheduled for this day.
              </Typography>
            )}
            {selectedDay && selectedRows.length > 0 && (
              <>
                <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums", mt: 0.5, mb: 1 }}>
                  {money(selectedRows.reduce((s, r) => s + r.amount, 0))}
                </Typography>
                <Stack divider={<Divider flexItem />} spacing={1}>
                  {selectedRows.map((r) => <PayoutRow key={r.order_id || r.sale_id} row={r} />)}
                </Stack>
              </>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
                TBD ({tbd.length})
              </Typography>
              <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {money(tbd.reduce((s, r) => s + r.amount, 0))}
              </Typography>
            </Stack>
            {tbd.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Every open order has a payout date.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={1} sx={{ maxHeight: 320, overflowY: "auto" }}>
                {tbd.map((r) => <PayoutRow key={r.order_id || r.sale_id} row={r} compact />)}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>

      {loading && funds.rows.length === 0 && (
        <Box sx={{ position: "fixed", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", px: 2, py: 1, borderRadius: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Loading…</Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function PayoutRow({ row, compact = false }) {
  // ``no_eta`` is the catch-all bucket now; unknown kinds fall back
  // there so a server-side schema bump never crashes the row renderer.
  const meta = KIND_META[row.kind] || KIND_META.no_eta;
  const orderId = row.order_id;
  const ebayHref = orderId
    ? `https://www.ebay.com/mesh/ord/details?orderid=${encodeURIComponent(String(orderId).trim())}`
    : null;
  // Many on-hold transactions belong to eBay sales BidRL hasn't synced
  // yet, so ``row.title`` is empty. Fall back through buyer → order id
  // so every row shows *something* useful and stays clickable. Keep the
  // monospace style on the order_id fallback so it reads as an ID.
  const title = row.title;
  const subtitle = row.buyer ? `Buyer: ${row.buyer}` : (orderId || null);
  const headline = title || (row.buyer ? `Buyer: ${row.buyer}` : null) || (orderId || "—");
  const isFallback = !title;

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontStyle: isFallback ? "italic" : "normal",
            fontFamily: (isFallback && !row.buyer) ? '"Geist Mono", monospace' : "inherit",
            fontSize: (isFallback && !row.buyer) ? 12 : "inherit",
          }}
        >
          {ebayHref ? (
            <a
              href={ebayHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted #999" }}
            >
              {headline}
            </a>
          ) : headline}
        </Typography>
        {/* Subtitle: order_id (when title was the headline) OR
            empty (when fallback already used the order_id). Lets the
            user see the order id even when the headline is the title. */}
        {!isFallback && orderId && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: '"Geist Mono", monospace', display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {orderId}{row.buyer ? ` · ${row.buyer}` : ""}
          </Typography>
        )}
        {isFallback && row.buyer && orderId && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: '"Geist Mono", monospace', display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {orderId}
          </Typography>
        )}
        <Stack direction="row" gap={0.5} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap">
          <Chip size="small" label={meta.label} color={meta.color} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
          {!compact && row.carrier && (
            <Chip size="small" variant="outlined" label={row.carrier.toUpperCase()} sx={{ height: 18, fontSize: 9, fontWeight: 700 }} />
          )}
          {!compact && row.status && row.status !== "unknown" && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {row.status.replace(/_/g, " ")}
            </Typography>
          )}
        </Stack>
      </Box>
      <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
        {money(row.amount)}
      </Typography>
    </Stack>
  );
}
